import { useRef, useState, useEffect } from "react";
import { ImagePlus, Trash2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * 🖼️ Merchant Logo Uploader
 * -------------------------
 * Drag-and-drop / click-to-upload picker. Uploads to Emergent Object Storage
 * via POST /api/auth/logo. The stored image auto-swaps the default 🛸
 * wordmark in both the Nebula PDF header band (client-side jsPDF) and the
 * Resend HTML receipt header (server-side).
 */
export function MerchantLogoUploader() {
  const { user, checkAuth } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (user?.merchant_logo_ext && user?.user_id) {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      setPreviewUrl(`${base}/api/auth/logo/${user.user_id}.${user.merchant_logo_ext}?t=${user.merchant_logo_updated_at || ""}`);
    } else {
      setPreviewUrl(null);
    }
  }, [user?.merchant_logo_ext, user?.user_id, user?.merchant_logo_updated_at]);

  const handleFiles = async (files) => {
    if (!files?.length || busy) return;
    const file = files[0];
    if (!/(image\/png|image\/jpeg|image\/webp|image\/svg\+xml)/.test(file.type)) {
      toast.error("Only PNG / JPG / WebP / SVG");
      return;
    }
    if (file.size > 512 * 1024) {
      toast.error("Logo must be under 512 KB");
      return;
    }
    setBusy(true);
    const toastId = toast.loading("🖼️ Uploading merchant logo…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/auth/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await checkAuth?.();
      setPreviewUrl(`${data.logo_url}?t=${Date.now()}`);
      toast.success("Logo saved · PDF + email header hot-swapped", { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed", { id: toastId });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (busy) return;
    setBusy(true);
    const toastId = toast.loading("Removing logo…");
    try {
      await api.delete("/auth/logo");
      await checkAuth?.();
      setPreviewUrl(null);
      toast.success("Logo cleared · default wordmark restored", { id: toastId });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed", { id: toastId });
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      data-testid="profile-merchant-logo"
      className="rounded-2xl p-4 border"
      style={{
        background: "linear-gradient(180deg, rgba(15,7,45,0.72), rgba(30,15,60,0.72))",
        border: "1px solid rgba(139,92,246,0.35)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.28em] text-fuchsia-300">
          <ImagePlus size={12} />
          🖼️ Custom Corporate Logo Wordmark
        </div>
        <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-emerald-300">
          <ShieldCheck size={10} /> Hot-swaps PDF + email
        </span>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-stretch">
        <div
          onDragOver={(e) => { e.preventDefault(); setHover(true); }}
          onDragLeave={() => setHover(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="profile-logo-dropzone"
          className={`flex-1 rounded-xl border-2 border-dashed cursor-pointer transition-colors flex flex-col items-center justify-center py-10 px-4 ${hover ? "border-fuchsia-400 bg-fuchsia-500/10" : "border-fuchsia-500/35 hover:border-fuchsia-500/60 hover:bg-fuchsia-500/5"}`}
        >
          <ImagePlus size={22} className="text-fuchsia-300 mb-2" />
          <div className="text-white text-xs font-semibold">Drag & drop your logo</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-1">
            PNG · JPG · WebP · SVG · &lt; 512 KB
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="profile-logo-input"
          />
        </div>

        <div
          className="rounded-xl border p-3 flex flex-col items-center justify-center min-w-[220px]"
          style={{ border: "1px solid rgba(139,92,246,0.35)", background: "rgba(255,255,255,0.03)" }}
          data-testid="profile-logo-preview"
        >
          <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mb-2">Live Preview</div>
          <div
            className="w-[180px] h-[80px] rounded-md flex items-center justify-center"
            style={{ background: "linear-gradient(135deg,#8b5cf6,#ec4899)" }}
          >
            {previewUrl ? (
              <img src={previewUrl} alt="Merchant logo" style={{ maxHeight: 60, maxWidth: 160 }} />
            ) : (
              <span className="text-white text-sm font-bold">🛸 {user?.name || "Merchant"}</span>
            )}
          </div>
          {previewUrl && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={busy}
              data-testid="profile-logo-remove"
              className="mt-3 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-rose-200 hover:text-rose-100 border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/25 rounded-md px-2 h-7 disabled:opacity-50"
            >
              <Trash2 size={10} /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default MerchantLogoUploader;
